import { encode } from "@googlemaps/polyline-codec";
import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { DRAW_POLYLINE_MIN_POINTS } from "@ryde/controls";
import { useCourseDtoActions, usePageContext } from "@ryde/redux";
import {
  CourseEntity,
  CourseFormFields,
  courseHasUnsavedStationChangeAtom,
  Log,
  OSRM_MAX_POINTS_FOR_SNAP_MATCHING,
  OsrmApi,
  TLatLngTuple,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { decodePolylineOrNull } from "./maplibre-polyline-edit.utils";
import { useMapLibrePolylineDrawClicks } from "./use-maplibre-polyline-draw-clicks";
import { IUseMapLibrePolylineEditResult } from "./use-maplibre-polyline-edit.models";
import { useMapLibrePolylineFormSync } from "./use-maplibre-polyline-form-sync";

/**
 * Draw / vertex-edit / snap state for the course polyline, kept out of the component so it renders only the
 * toolbar and the map layers. Draw and edit are mutually exclusive: each entry point refuses while the other
 * mode is live, which is what lets the two in-progress geometries (`drawingPoints` and the editor's own
 * handles) never contend for `finalPositions`.
 */
export const useMapLibrePolylineEdit = ({
  positions,
  onDrawStart,
  onDrawEnd,
  onDrawingStateChange,
}: {
  positions: TLatLngTuple[];
  onDrawStart?: () => void;
  onDrawEnd?: (newPositionsUrl: string) => void;
  onDrawingStateChange?: (
    isDrawing: boolean,
    addPointCallback?: (lat: number, lng: number) => void,
  ) => void;
}): IUseMapLibrePolylineEditResult => {
  const { setValue, watch } = useFormContext<CourseEntity>();
  const { t } = useTranslation();
  const { entity } = usePageContext();
  const { courseRebuildRouteHandler } = useCourseDtoActions();

  const [isDrawing, setIsDrawing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeletePointMode, setIsDeletePointMode] = useState(false);
  const [drawingPoints, setDrawingPoints] = useState<TLatLngTuple[]>([]);
  const [finalPositions, setFinalPositions] =
    useState<TLatLngTuple[]>(positions);

  // Draw-mode clicks arrive from a map listener, outside React's render, so the in-progress points are also
  // held in a ref — reading state there would append to whatever snapshot the listener closed over.
  const drawingPointsRef = useRef<TLatLngTuple[]>([]);
  const editedPositionsRef = useRef<TLatLngTuple[]>([]);
  const toolbarRef = useRef<HTMLDivElement | null>(null);

  const isManualPolylineEdit = watch(CourseFormFields.isManualPolylineEdit);
  const routePolylineFromForm = watch(CourseFormFields.routePolyline);
  const courseHasUnsavedStationChange = useRecoilValue(
    courseHasUnsavedStationChangeAtom,
  );
  const isCourse = entity === "course";

  const commitPolyline = useEvent((nextPositions: TLatLngTuple[]): string => {
    const encoded = encode(nextPositions.map(([lat, lng]) => [lat, lng]));

    setValue(CourseFormFields.routePolyline, encoded, {
      shouldDirty: true,
      shouldTouch: true,
    });
    setValue(CourseFormFields.isManualPolylineEdit, true, {
      shouldDirty: true,
      shouldTouch: true,
    });
    setFinalPositions(nextPositions);

    return encoded;
  });

  const setDrawingPointsSynced = useEvent((points: TLatLngTuple[]) => {
    drawingPointsRef.current = points;
    setDrawingPoints(points);
  });

  const addPointToPolyline = useEvent((lat: number, lng: number) => {
    if (!isDrawing) {
      return;
    }
    setDrawingPointsSynced([...drawingPointsRef.current, [lat, lng]]);
  });

  const handleUndoLastPoint = useEvent(() => {
    if (!isDrawing || !drawingPointsRef.current.length) {
      return;
    }
    setDrawingPointsSynced(drawingPointsRef.current.slice(0, -1));
  });

  const handleDrawStart = useEvent(() => {
    if (isDrawing || isEditing) {
      return;
    }
    setDrawingPointsSynced([]);
    setIsDrawing(true);
    onDrawStart?.();
  });

  const handleDrawEnd = useEvent(() => {
    if (!isDrawing) {
      return;
    }

    const points = drawingPointsRef.current;
    setIsDrawing(false);

    if (points.length >= DRAW_POLYLINE_MIN_POINTS) {
      // Commit as its own statement. `onDrawEnd?.(commitPolyline(points))` skips the WHOLE call expression —
      // arguments included — when `onDrawEnd` is undefined, which is the normal case here, so the save wrote
      // nothing at all and the form-sync effect then restored the previous route.
      const encodedPolyline = commitPolyline(points);
      onDrawEnd?.(encodedPolyline);
    }

    setDrawingPointsSynced([]);
  });

  const handleDrawCancel = useEvent(() => {
    if (!isDrawing) {
      return;
    }
    setIsDrawing(false);
    setDrawingPointsSynced([]);
    setFinalPositions(positions);
  });

  const handleEditedPositionsChange = useEvent(
    (newPositions: TLatLngTuple[]) => {
      editedPositionsRef.current = newPositions;
    },
  );

  const handleEditStart = useEvent(() => {
    if (isDrawing || isEditing) {
      return;
    }
    editedPositionsRef.current = finalPositions;
    setIsEditing(true);
  });

  const handleEditSave = useEvent(() => {
    if (!isEditing) {
      return;
    }

    const editedPositions = editedPositionsRef.current;

    if (editedPositions.length >= DRAW_POLYLINE_MIN_POINTS) {
      const encodedPolyline = commitPolyline(editedPositions);
      onDrawEnd?.(encodedPolyline);
    } else {
      Log.warnAndToast(
        t("errors.POLYLINE_TOO_FEW_POINTS", { min: DRAW_POLYLINE_MIN_POINTS }),
      );
    }

    setIsEditing(false);
  });

  const handleEditCancel = useEvent(() => {
    if (!isEditing) {
      return;
    }
    setIsEditing(false);
    editedPositionsRef.current = [];
  });

  const handleToggleDeletePointMode = useEvent(() => {
    setIsDeletePointMode(previous => !previous);
  });

  const handleSnapRouteClick = useEvent(async () => {
    if (!finalPositions.length) {
      Log.error("No points to snap");
      return;
    }

    try {
      const response = await OsrmApi.getSnapMatchedPolyline(
        finalPositions.map(([lat, lng]) => [lng, lat]),
      );
      const snappedPolyline = response?.matchings?.[0]?.geometry;

      if (response?.code !== "Ok" || !snappedPolyline) {
        return;
      }

      const snappedPositions = decodePolylineOrNull(
        snappedPolyline,
        "Error decoding snapped routePolyline:",
      );

      if (snappedPositions?.length) {
        commitPolyline(snappedPositions);
      }
    } catch (error) {
      Log.warnAndToast(t("errors.SNAP_ROUTE_ERROR"), error);
    }
  });

  const handleRemoveManualPolyline = useEvent(async () => {
    if (!isCourse || !isManualPolylineEdit) {
      return;
    }

    if (isDrawing) {
      setIsDrawing(false);
      setDrawingPointsSynced([]);
    }

    const result = await courseRebuildRouteHandler(false);

    if (!result.success) {
      warnAndToastSimpleResult(result, t);
      return;
    }

    setValue(CourseFormFields.routePolyline, result.data?.routePolyline, {
      shouldDirty: true,
      shouldTouch: true,
    });
    setValue(CourseFormFields.isManualPolylineEdit, false, {
      shouldDirty: true,
      shouldTouch: true,
    });

    const rebuiltPositions = decodePolylineOrNull(
      result.data?.routePolyline,
      "Error decoding routePolyline after rebuild:",
    );

    if (rebuiltPositions?.length) {
      setFinalPositions(rebuiltPositions);
    } else {
      setFinalPositions([]);
    }
  });

  // Delete-point is a sub-mode of vertex edit; leaving edit must not leave it armed for the next session.
  useEffect(() => {
    if (!isEditing) {
      setIsDeletePointMode(false);
    }
  }, [isEditing]);

  useEffect(() => {
    onDrawingStateChange?.(
      isDrawing,
      isDrawing ? addPointToPolyline : undefined,
    );
  }, [isDrawing, addPointToPolyline, onDrawingStateChange]);

  useMapLibrePolylineFormSync({
    positions,
    routePolylineFromForm,
    isManualPolylineEdit: Boolean(isManualPolylineEdit),
    hasUnsavedStationsChange: isCourse && courseHasUnsavedStationChange,
    isDrawing,
    isEditing,
    onSyncedPositions: setFinalPositions,
  });

  useMapLibrePolylineDrawClicks({
    isDrawing,
    toolbarRef,
    onAddPoint: addPointToPolyline,
  });

  return {
    toolbarRef,
    isDrawing,
    isEditing,
    isDeletePointMode,
    finalPositions,
    drawingPoints,
    canFinishDraw: drawingPoints.length >= DRAW_POLYLINE_MIN_POINTS,
    isSnapButtonVisible:
      !isDrawing &&
      !isEditing &&
      finalPositions.length <= OSRM_MAX_POINTS_FOR_SNAP_MATCHING,
    isRemoveManualPolylineVisible:
      !isDrawing && !isEditing && isCourse && Boolean(isManualPolylineEdit),
    isManualPolylineEdit: Boolean(isManualPolylineEdit),
    handleDrawStart,
    handleDrawEnd,
    handleDrawCancel,
    handleUndoLastPoint,
    handleEditStart,
    handleEditSave,
    handleEditCancel,
    handleEditedPositionsChange,
    handleToggleDeletePointMode,
    handleSnapRouteClick,
    handleRemoveManualPolyline,
  };
};
